import { Box, Grid, Tooltip } from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'
import { emptyUserDTO, fromModel, User, UserDTO } from 'modules/users/models/User'
import { UserService } from '../../modules/users/services/UserService'
import { LOGGED_USER_SERVICE_KEY, USER_SERVICE_KEY } from '../../modules/users'
import { getUserContainer } from '../../container/user-module'
import styles from './dashboard.module.css'
import { useTranslation } from 'react-i18next'
import iconCalendar from '../../assets/dashboard/calendar.svg'
import iconChat from '../../assets/dashboard/chats.svg'
import iconTraining from '../../assets/dashboard/trainings.svg'
import iconPatient from '../../assets/dashboard/patient.svg'
import iconStatistics from '../../assets/dashboard/statistics.svg'
import iconEdit from '../../assets/table_icons/edit.svg'
import iconMedication from '../../assets/dashboard/treatments.svg'
import { Statistics } from '../statistics'
import { navigate } from '@reach/router'
import {
  ROUTE_DASHBOARD_TRAINING,
  ROUTE_MESSENGER,
  ROUTE_TREATMENTS,
  ROUTE_USERS_CREATE,
} from '../../routes/routes-constants'
import { AppButton, ButtonTheme } from '../../components/app-button/AppButton'
import { WearableAssignment } from './WearableAssignment'
import { Query, QueryParam } from '../../common/api/Query'
import { emptyList, ItemList } from '../../common/models/ItemList'
import { Table } from '../treatments/Table'
import { Skeleton } from '@material-ui/lab'
import { IUserTrainingService, USER_TRAINING_SERVICE_KEY } from '../../modules/training'
import { UserTraining } from '../../modules/training/models/UserTraining'
import { getTrainingContainer } from '../../container/training-module'
import { ConversationService } from '../../modules/messenger/services/ConversationService'
import { CONVERSATION_SERVICE_KEY } from '../../modules/messenger'
import { getMessengerContainer } from '../../container/messenger-module'
import { Conversation, ConversationQuery } from '../../modules/messenger/models/Conversation'
import { AssignTrainingForm } from '../training/AssignTrainingForm'
import { FormStats } from '../forms-analytics/FormStats'
import { FormStat } from '../forms-analytics/FormStats'
import { IAnalyticsService, ANALYTICS_SERVICE_KEY } from '../../modules/forms'
import { getFormContainer } from '../../container/form-module'
import { useUserRoles } from '../../modules/users/hooks/useUserRoles'
import { ILoggedUserService } from 'modules/users/services/LoggedUserService'
import FormStatsChart from 'components/form-stats-chart/FormStatsChart'
import { PageLayout } from 'pages/layout'

type DashboardProps = {
  id?: string
}

interface AlarmStatus {
  medication: boolean
  food: boolean
  sleep: boolean
}

const userContainer = getUserContainer()
const userService = userContainer.get<UserService>(USER_SERVICE_KEY)
const loggedUserService = userContainer.get<ILoggedUserService>(LOGGED_USER_SERVICE_KEY)
const userTrainingService =
  getTrainingContainer().get<IUserTrainingService>(USER_TRAINING_SERVICE_KEY)
const messengerContainer = getMessengerContainer()
const conversationsService = messengerContainer.get<ConversationService>(CONVERSATION_SERVICE_KEY)
const formContainer = getFormContainer()
const analyticsService = formContainer.get<IAnalyticsService>(ANALYTICS_SERVICE_KEY)

export const Dashboard = (props: DashboardProps) => {
  const { t } = useTranslation()
  const [user, setUser] = useState<User>()
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [doctor, setDoctor] = useState<UserDTO>(emptyUserDTO())
  const [userTraining, setUserTraining] = useState<ItemList<UserTraining>>(emptyList())
  const [conversationCollection, setConversationCollection] = useState<Conversation[]>([])
  const [openModalAssignTraining, setOpenModalAssignTraining] = useState<boolean>(false)
  const [medicationStats, setMedicationStats] = useState<FormStat[]>([])
  const [foodStats, setFoodStats] = useState<FormStat[]>([])
  const [sleepStats, setSleepStats] = useState<FormStat[]>([])
  const [alarmStatus, setAlarmStatus] = useState<AlarmStatus>({
    medication: false,
    food: false,
    sleep: false,
  })
  const [isLoadingStats, setIsLoadingStats] = useState<boolean>(true)

  const loggedUser = loggedUserService.get()
  const { canEditPatientData, canCreateTreatment, canCreateTraining } = useUserRoles(loggedUser)

  const uniqueTrainingsCount = useMemo(
    () => new Set(userTraining?.items.map((t) => t.trainingID)).size,
    [userTraining]
  )

  useEffect(() => {
    if (!props.id) {
      return
    }

    userService.getByID(props.id).subscribe((res) => {
      if (res) {
        setUser(res)
        setIsLoading(false)
      }
    })
  }, [props.id])

  useEffect(() => {
    if (!user?.assignedID) return

    userService.getByID(user.assignedID).subscribe((res) => {
      if (res) {
        setDoctor(fromModel(res))
      }
    })
  }, [user?.assignedID])

  useEffect(() => {
    if (!isLoading || !props?.id) {
      return
    }
    conversationsService
      .getFilteredItems(
        new Query({
          query: [new QueryParam<ConversationQuery>('userID', props.id || '')],
        })
      )
      .subscribe((res) => {
        setConversationCollection(res)
        setIsLoading(false)
      })
  }, [isLoading])

  useEffect(() => {
    if (!props.id) return

    const userId = props.id

    const loadStats = async () => {
      try {
        const [medication, food, sleep] = await Promise.all([
          analyticsService.getMedicationStats(userId).toPromise(),
          analyticsService.getFoodStats(userId).toPromise(),
          analyticsService.getSleepStats(userId).toPromise(),
        ])

        setMedicationStats(medication || [])
        setFoodStats(food || [])
        setSleepStats(sleep || [])
      } catch (error) {
        console.error('Error loading stats:', error)
      } finally {
        setIsLoadingStats(false)
      }
    }

    loadStats()
  }, [props.id])

  useEffect(() => {
    if (!props.id) return

    const userId = props.id

    const loadAlarmStatus = async () => {
      try {
        if (analyticsService.getAlarmStatus) {
          const alarmData = await analyticsService.getAlarmStatus(userId).toPromise()
          if (alarmData) {
            setAlarmStatus(alarmData)
          }
        } else {
          console.warn('getAlarmStatus method not available in analyticsService')
          setAlarmStatus({
            medication: false,
            food: false,
            sleep: false,
          })
        }
      } catch (error) {
        console.error('Error loading alarm status:', error)
        setAlarmStatus({
          medication: false,
          food: false,
          sleep: false,
        })
      }
    }

    loadAlarmStatus()
  }, [props.id])

  useEffect(() => {
    if (!props.id) return

    const userId = props.id

    userTrainingService
      .getFilteredList(
        new Query({
          query: [
            {
              name: 'userId',
              value: userId,
            },
          ],
        })
      )
      .subscribe((res) => {
        setUserTraining(res)
      })
  }, [props.id, openModalAssignTraining])

  const createTreatment = () => {
    if (!props.id) return
    navigate(`${ROUTE_TREATMENTS}`, { state: { userID: props.id } })
  }

  const handlerCloseModalAssignTraining = () => {
    setOpenModalAssignTraining(false)
  }

  const calculateAge = (dob: Date) => {
    if (!dob) return ''
    const today = new Date()
    const birthDate = new Date(dob)
    let age = today.getFullYear() - birthDate.getFullYear()
    const monthDiff = today.getMonth() - birthDate.getMonth()
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--
    }
    return age
  }

  return (
    <PageLayout withCard={false}>
      <div className={styles.pageWrapper}>
        <Box className={styles.container}>
          {openModalAssignTraining && props.id && (
            <AssignTrainingForm
              open={openModalAssignTraining}
              handleClose={handlerCloseModalAssignTraining}
              id={props.id}
            />
          )}
          <Grid container spacing={2}>
            <Grid item xs={12} lg={6}>
              {props.id && <WearableAssignment userId={props.id} />}
            </Grid>

            {/*             <Grid item xs={12} lg={6}>
              <Box className={styles.containerBoxLittle}>
                <Box className={styles.titleBox}>
                  <Box
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-start',
                      alignItems: 'flex-start',
                    }}>
                    <p className={styles.titleText}>
                      {isLoading ? <Skeleton width={'10rem'} /> : t('messages')}
                    </p>
                    <p className={styles.textAmount}>
                      {isLoading ? <Skeleton width={'2rem'} /> : conversationCollection.length}
                    </p>
                  </Box>
                  {isLoading ? (
                    <Skeleton variant="circle" width={40} height={40} />
                  ) : (
                    <img
                      className={styles.imageBox}
                      src={iconChat}
                      alt={t('chatIcon')}
                      width={80}
                      height={80}
                    />
                  )}
                </Box>
                <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
                  <Box style={{ marginBottom: 8 }}>
                    {isLoading ? (
                      <Skeleton />
                    ) : (
                          <AppButton
                            label={t('view')}
                            theme={ButtonTheme.NewPrimary}
                            handler={() => navigate(ROUTE_MESSENGER)}
                            type={'button'}
                          />
                    )}
                  </Box>
                  <Box className={styles.updateBox}>
                    <p className={styles.titleUpdateBox}>{t('updated')}</p>
                    <img
                      className={styles.imageCalendar}
                      src={iconCalendar}
                      alt={t('calendarIcon')}
                      width={20}
                      height={20}
                    />
                  </Box>
                </Box>
              </Box>
            </Grid> */}

            <Grid item xs={12} lg={6}>
              <Box className={styles.containerBoxLittle}>
                <Box className={styles.titleBox}>
                  <Box
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-start',
                      alignItems: 'flex-start',
                    }}
                  >
                    <p className={styles.titleText}>
                      {isLoading ? <Skeleton width={'10rem'} /> : t('training')}
                    </p>
                    <p className={styles.textAmount}>
                      {isLoading ? <Skeleton width={'2rem'} /> : uniqueTrainingsCount}
                    </p>
                  </Box>
                  {isLoading ? (
                    <Skeleton variant="circle" width={40} height={40} />
                  ) : (
                    <img
                      className={styles.imageBox}
                      src={iconTraining}
                      alt={t('trainingIcon')}
                      width={80}
                      height={80}
                    />
                  )}
                </Box>
                <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
                  <Box style={{ marginBottom: 8 }}>
                    {isLoading ? (
                      <Skeleton />
                    ) : (
                      <>
                        {uniqueTrainingsCount > 0 ? (
                          <AppButton
                            label={t('seeTrainings')}
                            theme={ButtonTheme.NewPrimary}
                            handler={() => navigate(`${ROUTE_DASHBOARD_TRAINING}/${user?.id}`)}
                            type={'button'}
                          />
                        ) : (
                          <>
                            {canCreateTraining && (
                              <AppButton
                                label={t('assign')}
                                theme={ButtonTheme.NewPrimary}
                                handler={() => setOpenModalAssignTraining(true)}
                                type={'button'}
                              />
                            )}
                          </>
                        )}
                      </>
                    )}
                  </Box>
                  <Box className={styles.updateBox}>
                    <p className={styles.titleUpdateBox}>{t('updated')}</p>
                    <img
                      className={styles.imageCalendar}
                      src={iconCalendar}
                      alt={t('calendarIcon')}
                      width={20}
                      height={20}
                    />
                  </Box>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} lg={12}>
              <Box className={styles.containerBoxStatistics}>
                <Box className={styles.boxMedications}>
                  <Box
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-start',
                      alignItems: 'flex-start',
                    }}
                  >
                    <p className={styles.titleText}>{t('medication')}</p>
                  </Box>
                  <img
                    className={styles.imageBox}
                    src={iconMedication}
                    alt={t('medicationIcon')}
                    width={70}
                    height={70}
                  />
                </Box>
                <Table id={user?.id} />
                <Box display={'flex'} justifyContent={'space-between'}>
                  <Box style={{ marginBottom: 8 }}>
                    {isLoading ? (
                      <Skeleton />
                    ) : (
                      <>
                        {canCreateTreatment && (
                          <AppButton
                            label={t('addTreatment')}
                            theme={ButtonTheme.NewPrimary}
                            handler={createTreatment}
                            type={'button'}
                          />
                        )}
                      </>
                    )}
                  </Box>
                  <Box className={styles.updateBox}>
                    <p className={styles.titleUpdateBox}>{t('updated')}</p>
                    <img
                      className={styles.imageCalendar}
                      src={iconCalendar}
                      alt={t('calendarIcon')}
                      width={20}
                      height={20}
                    />
                  </Box>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} lg={12}>
              <Box className={styles.containerBoxStatistics}>
                <FormStats
                  medicationStats={medicationStats}
                  foodStats={foodStats}
                  sleepStats={sleepStats}
                  alarmStatus={alarmStatus}
                  userId={props.id || ''}
                  isLoading={isLoadingStats}
                />
              </Box>
            </Grid>

            <Grid item xs={12} lg={4}>
              <Box className={styles.containerBoxLarge}>
                <Box className={styles.titleBoxPatientStatistics}>
                  <Box
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-start',
                      alignItems: 'flex-start',
                    }}
                  >
                    <p className={styles.titleText}>{t('patient')}</p>
                  </Box>
                  <img
                    className={styles.imageBox}
                    src={iconPatient}
                    alt={t('patientIcon')}
                    width={70}
                    height={70}
                  />
                </Box>

                <Box className={styles.patientInfoContainer}>
                  <Box className={styles.patientInfoRow}>
                    <span className={styles.patientInfoLabel}>{t('name')}:</span>
                    <span className={styles.patientInfoValue}>
                      {isLoading ? (
                        <Skeleton width={'8rem'} />
                      ) : (
                        `${user?.firstName || ''} ${user?.lastName || ''}`.trim()
                      )}
                    </span>
                  </Box>

                  <Box className={styles.patientInfoRow}>
                    <span className={styles.patientInfoLabel}>{t('age')}:</span>
                    <span className={styles.patientInfoValue}>
                      {isLoading ? (
                        <Skeleton width={'4rem'} />
                      ) : user?.dob ? (
                        calculateAge(user.dob)
                      ) : (
                        ''
                      )}
                    </span>
                  </Box>

                  <Box className={styles.patientInfoRow}>
                    <span className={styles.patientInfoLabel}>{t('historyNumber')}:</span>
                    <span className={styles.patientInfoValue}>
                      {isLoading ? <Skeleton width={'6rem'} /> : user?.historyRecordNumber}
                    </span>
                  </Box>

                  <Box className={styles.patientInfoRow}>
                    <span className={styles.patientInfoLabel}>{t('assignTo')}:</span>
                    <span className={styles.patientInfoValue}>
                      {isLoading ? (
                        <Skeleton width={'8rem'} />
                      ) : (
                        `${doctor.firstName} ${doctor.lastName}`.trim()
                      )}
                    </span>
                  </Box>

                  <Box className={styles.patientInfoRow}>
                    <span className={styles.patientInfoLabel}>{t('diet')}:</span>
                    <span className={styles.patientInfoValue}>
                      {isLoading ? (
                        <Skeleton width={'6rem'} />
                      ) : user?.diet && user.diet.types.length > 0 ? (
                        `${user.diet.types.map((type) => t(type)).join(', ')}${
                          user.diet.texture ? ` - ${t(user.diet.texture)}` : ''
                        }`
                      ) : (
                        ''
                      )}
                    </span>
                  </Box>

                  <Box className={styles.patientInfoRow}>
                    <span className={styles.patientInfoLabel}>{t('pathologies')}:</span>
                    <span className={styles.patientInfoValue}>
                      {isLoading ? (
                        <Skeleton width={'8rem'} />
                      ) : user?.patologies && user.patologies.length > 0 ? (
                        user.patologies
                          .map((pathology) => {
                            if (pathology === 'others') {
                              return user.otherPatology ? t(user.otherPatology) : t('others')
                            }
                            return t(pathology)
                          })
                          .join(', ')
                      ) : (
                        ''
                      )}
                    </span>
                  </Box>

                  <Box className={styles.patientInfoRow}>
                    <span className={styles.patientInfoLabel}>{t('diagnosis')}:</span>
                    <span className={styles.patientInfoValue}>
                      {isLoading ? <Skeleton width={'10rem'} /> : user?.diagnosis}
                    </span>
                  </Box>

                  <Box className={styles.patientInfoRow}>
                    <span className={styles.patientInfoLabel}>{t('diagnosisSummary')}:</span>
                    <span className={styles.patientInfoValue}>
                      {isLoading ? <Skeleton width={'12rem'} /> : user?.diagnosisSummary}
                    </span>
                  </Box>
                </Box>

                {canEditPatientData && (
                  <Box className={styles.editButtonContainer}>
                    <Tooltip
                      onClick={() => navigate(`${ROUTE_USERS_CREATE}/${user?.id}`)}
                      title={`${t('edit')}`}
                    >
                      <img
                        style={{ cursor: 'pointer' }}
                        className={styles.imageBox}
                        src={iconEdit}
                        alt={t('editIcon')}
                        width={38}
                        height={38}
                      />
                    </Tooltip>
                  </Box>
                )}
              </Box>
            </Grid>

            <Grid item lg={8}>
              <Box className={styles.containerBoxStatistics}>
                <Box className={styles.titleBoxPatientStatistics}>
                  <p className={styles.titleText}>{t('statistics')}</p>
                  <img
                    style={{ marginRight: 40 }}
                    className={styles.imageBox}
                    src={iconStatistics}
                    alt={t('statisticsIcon')}
                    width={70}
                    height={70}
                  />
                </Box>
                {props.id && (
                  <Statistics
                    dashboard={true}
                    stylesText={styles.statisticsText}
                    stylesHistorial={styles.styleHistorialButton}
                    styleInfoBox={styles.styleInfoBox}
                    id={props?.id}
                  />
                )}
                <Box display={'flex'} justifyContent={'flex-end'}>
                  <Box className={styles.updateBox}>
                    <p className={styles.titleUpdateBox}>{t('updated')}</p>
                    <img
                      className={styles.imageCalendar}
                      src={iconCalendar}
                      alt={t('calendarIcon')}
                      width={20}
                      height={20}
                    />
                  </Box>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} lg={12}>
              <Box className={styles.containerBoxStatistics}>
                <Box className={styles.titleBoxPatientStatistics}>
                  <p className={styles.titleText}>{t('evolution')}</p>
                </Box>
                <FormStatsChart userId={props.id || ''} />
              </Box>
            </Grid>
          </Grid>
        </Box>
      </div>
    </PageLayout>
  )
}
